import React, { useState, useRef, useEffect } from 'react';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ur', label: 'Urdu' },
  { code: 'zh', label: 'Chinese (Mandarin)' },
  { code: 'it', label: 'Italian' },
  { code: 'ko', label: 'Korean' },
];

export default function LanguageDropdown({ currentLanguage, onLanguageChange, theme }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      window.addEventListener('keydown', handleKey);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const themeStyles = {
    light: {
      dropdown: { background: '#fff6ec', color: '#232323', boxShadow: '0 2px 16px #2222' },
      item: { color: '#232323' },
      active: { background: '#f5b443', color: '#fff' },
    },
    dark: {
      dropdown: { background: '#1e1e1e', color: '#fff', boxShadow: '0 2px 16px #000a' },
      item: { color: '#fff' },
      active: { background: '#f5b443', color: '#1a1a1a' },
    },
  };
  // Fallback to light if theme is not set or is 'device'
  const styles = themeStyles[theme] || themeStyles.light;

  // --- Globe button style to match ? and theme buttons ---
  const globeBtnStyle = {
    height: 38,
    width: 38,
    minWidth: 38,
    minHeight: 38,
    borderRadius: '50%',
    background: 'var(--color-bg, #fff)',
    color: 'var(--color-text, #232323)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    marginLeft: 4,
    marginRight: 0,
    boxShadow: '0 1.5px 8px #0002',
    cursor: 'pointer',
    transition: 'background 0.18s, color 0.18s, border 0.18s, box-shadow 0.18s',
    outline: 'none',
    position: 'relative',
  };

  return (
    <div ref={ref} style={{ display: 'inline-block', position: 'relative', verticalAlign: 'middle' }}>
      <button
        aria-label="Change language"
        style={globeBtnStyle}
        onClick={() => setOpen((v) => !v)}
        tabIndex={0}
        className="dashboard-btn language-btn"
      >
        {/* Centered globe icon SVG */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ display: 'block', margin: 'auto' }}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M2 12h20M12 2c2.5 2.5 2.5 17.5 0 20M12 2c-2.5 2.5-2.5 17.5 0 20" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      {open && (
        <div
          className="language-dropdown"
          style={{
            position: 'absolute',
            top: 44,
            right: 0,
            zIndex: 1000,
            minWidth: 170,
            borderRadius: 14,
            padding: '0.5rem 0.5rem',
            ...styles.dropdown,
          }}
        >
          {LANGUAGES.map((lang) => (
            <div
              key={lang.code}
              onClick={() => {
                onLanguageChange(lang.code);
                setOpen(false);
              }}
              style={{
                padding: '0.6rem 1.1rem',
                borderRadius: 10,
                fontWeight: 500,
                fontSize: '1.08rem',
                cursor: 'pointer',
                marginBottom: 2,
                textAlign: 'left',
                ...(currentLanguage === lang.code ? styles.active : styles.item),
              }}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onLanguageChange(lang.code);
                  setOpen(false);
                }
              }}
            >
              {lang.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 